// A questionnaire as the office reads it: the client's answers under the
// questionnaire's own sections, the files they uploaded, the consents
// they gave. Nothing here was written by a model - the labels are the
// office's, the words are the client's - so nothing wears the AI surface.
// Taking the row into work stays on the intake list, where the click is
// guarded against a double mint.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { IntakeRenderedAnswer, IntakeUploadedDocument } from "@duralex/shared";
import { downloadIntakePdf, fetchSubmission } from "../api/intake";
import { EntityLink } from "../../../components/chrome/EntityLink";
import { Skeleton, SkeletonGroup } from "../../../components/chrome/Skeleton";
import { fmtDate, fmtDay } from "../../../lib/dates";
import { languageName } from "../../../lib/intakeLanguages";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const WEIGHT: Record<string, string> = { "if-exists": " - if it exists", no: " - optional" };

function Answer({ q }: { q: IntakeRenderedAnswer }) {
  if (q.answer === null) return <span className="intake-answer-empty">(not answered)</span>;
  if (q.type === "longtext") return <div className="intake-description-block">{q.answer}</div>;
  if (q.type === "date") return <span className="tnum">{fmtDay(q.answer)}</span>;
  return <span>{q.answer}</span>;
}

function Rows({ questions }: { questions: IntakeRenderedAnswer[] }) {
  return (
    <div className="intake-answers">
      {questions.map((q) => (
        <div key={q.id} className="intake-answer">
          <div className="intake-answer-label">{q.label}</div>
          <div className="intake-answer-value">
            <Answer q={q} />
          </div>
        </div>
      ))}
    </div>
  );
}

function FileLine({ f }: { f: IntakeUploadedDocument }) {
  return (
    <div className="intake-file">
      <span>{f.fileName}</span>
      <span className="cc-form-hint">
        {fmtSize(f.size)} - {fmtDate(f.uploadedAt)}
      </span>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="cc-main">
      <div className="cc-body">{children}</div>
    </main>
  );
}

export function IntakeSubmission() {
  const { sessionId = "" } = useParams();
  const view = useQuery({
    queryKey: ["intake", "submission", sessionId],
    queryFn: () => fetchSubmission(sessionId),
    retry: false,
  });
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  if (view.isLoading) {
    return (
      <Shell>
        <SkeletonGroup label="Loading the answers">
          <Skeleton width="40%" height={20} />
          <Skeleton />
          <Skeleton width="80%" />
        </SkeletonGroup>
      </Shell>
    );
  }

  if (!view.data) {
    return (
      <Shell>
        <div className="cc-index-head">
          <span className="title">Answers</span>
        </div>
        <div role="alert" className="cc-form-error">
          {messageOf(view.error, "The answers could not be loaded.")}
        </div>
        <Link to="/intake" className="cc-link-btn">
          Back to intake
        </Link>
      </Shell>
    );
  }

  const v = view.data;
  const name = v.clientName?.trim() || "(no name)";
  const savePdf = async () => {
    setPdfError(null);
    setPdfBusy(true);
    try {
      await downloadIntakePdf(sessionId, v.exportName);
    } catch (err) {
      setPdfError(messageOf(err, "Could not download the PDF."));
    } finally {
      setPdfBusy(false);
    }
  };
  const caseType = v.categoryLabel ?? v.category ?? "no case type yet";
  const when =
    v.status === "submitted"
      ? `submitted ${fmtDate(v.submittedAt, "")}`.trim()
      : `not submitted - ${v.answered} of ${v.questions} answered` +
        (v.expiresAt ? `, open until ${fmtDate(v.expiresAt)}` : "");

  return (
    <Shell>
      <div className="cc-index-head">
        <span className="title">{name}</span>
        <span className="blurb">
          {caseType} - {when}
        </span>
      </div>

      <div className="intake-submission-facts">
        <div className="intake-done-row">
          <span className="intake-done-label">Case type</span>
          <span>{caseType}</span>
        </div>
        <div className="intake-done-row">
          <span className="intake-done-label">Language</span>
          <span>{v.language ? languageName(v.language) : "not chosen"}</span>
        </div>
        <div className="intake-done-row">
          <span className="intake-done-label">Email</span>
          <span>{v.email ?? "no email"}</span>
        </div>
        <div className="intake-done-row">
          <span className="intake-done-label">Source</span>
          <span>{v.source === "public" ? "Public form" : "Link from the office"}</span>
        </div>
        <div className="intake-done-row">
          <span className="intake-done-label">Matter</span>
          <span>
            {v.matterEid ? (
              <EntityLink kind="matter" id={v.matterEid} eID={v.matterEid}>
                <span className="tnum-mono">{v.matterEid}</span>
              </EntityLink>
            ) : v.matterId ? (
              "Taken into work, but the matter could not be found"
            ) : v.status === "submitted" ? (
              <>
                Not yet taken into work - <Link to="/intake">from the intake list</Link>
              </>
            ) : (
              "None yet"
            )}
          </span>
        </div>
      </div>
      <div className="cc-form-hint">
        Rendered against questionnaire version {v.templateVersion}, the one in use now - the client
        may have filled an earlier one.
      </div>
      <div className="intake-submission-actions">
        <button type="button" className="cc-link-btn" disabled={pdfBusy} onClick={savePdf}>
          Download PDF
        </button>
        {pdfError && (
          <div role="alert" className="cc-form-error">
            {pdfError}
          </div>
        )}
      </div>

      {/* The page's own headings carry "page-" - a template section id cannot. */}
      {v.sections.map((s) => (
        <section key={s.id} className="intake-answers-section" aria-labelledby={`sec-${s.id}`}>
          <h2 id={`sec-${s.id}`} className="intake-answers-title">
            {s.title}
          </h2>
          <Rows questions={s.questions} />
        </section>
      ))}

      {v.extra.length > 0 && (
        <section className="intake-answers-section" aria-labelledby="sec-page-extra">
          <h2 id="sec-page-extra" className="intake-answers-title">
            Other answers
          </h2>
          <div className="cc-form-hint">
            Answers to questions this case type is no longer asked - kept so nothing the client
            wrote is lost.
          </div>
          <Rows questions={v.extra} />
        </section>
      )}

      <section className="intake-answers-section" aria-labelledby="sec-page-documents">
        <h2 id="sec-page-documents" className="intake-answers-title">
          Documents
        </h2>
        <div className="intake-answers">
          {v.documents.checklist.map((d) => (
            <div key={d.id} className="intake-answer">
              <div className="intake-answer-label">
                {d.label}
                {WEIGHT[d.required] ?? ""}
              </div>
              <div className="intake-answer-value">
                {d.files.length === 0 ? (
                  v.status === "submitted" && d.required === "yes" ? (
                    <span className="intake-answer-missing">missing</span>
                  ) : (
                    <span className="intake-answer-empty">not provided</span>
                  )
                ) : (
                  d.files.map((f) => <FileLine key={f.id} f={f} />)
                )}
              </div>
            </div>
          ))}
          {v.documents.other.length > 0 && (
            <div className="intake-answer">
              <div className="intake-answer-label">Other files</div>
              <div className="intake-answer-value">
                {v.documents.other.map((f) => (
                  <FileLine key={f.id} f={f} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="intake-answers-section" aria-labelledby="sec-page-consents">
        <h2 id="sec-page-consents" className="intake-answers-title">
          Consents
        </h2>
        <div className="intake-answers">
          {v.consents.map((c) => (
            <div key={c.id} className="intake-answer">
              <div className="intake-answer-label">{c.given ? "Accepted" : "Not on record"}</div>
              <div className="intake-answer-value">{c.text}</div>
            </div>
          ))}
        </div>
        <div className="cc-form-hint">The wording is the questionnaire's current text.</div>
      </section>
    </Shell>
  );
}
